(() => {
  const instances = new WeakMap();
  const offerFor = (card) => card.querySelector('[data-rel-offer]:checked, input[type="hidden"][data-rel-offer]');
  const init = (root, preferredGroup) => {
    if (instances.has(root)) return;
    const groups = [...root.querySelectorAll('[data-rel-group]')];
    const cards = [...root.querySelectorAll('[data-relationship-card]')];
    if (!groups.length) return;
    const selected = new Set();
    let inCart = new Set((root.dataset.cartVariants || '').split(',').filter(Boolean));
    let busy = false;
    let cartRequest = 0;
    const tablist = root.querySelector('[data-rel-tabs]');
    const footer = root.querySelector('[data-rel-footer]');
    const submit = root.querySelector('[data-rel-submit]');
    const message = root.querySelector('[data-rel-message]');
    const sliders = new Map();
    const closePickers = () => root.querySelectorAll('[data-rel-picker]').forEach(picker => {
      picker.open = false;
      const menu = picker.querySelector('[popover]');
      if (menu?.matches(':popover-open')) menu.hidePopover();
    });
    if (root.dataset.slider === 'true') {
      root.classList.add('is-slider');
      groups.forEach(group => {
        const track = group.querySelector('[data-rel-track]');
        const items = [...track.querySelectorAll('[data-relationship-card]')];
        const browse = group.querySelector('[data-rel-browse]');
        const previous = group.querySelector('[data-rel-previous]');
        const next = group.querySelector('[data-rel-next]');
        track.tabIndex = 0;
        track.dataset.single = String(items.length === 1);
        const measure = () => {
          const bounds = track.getBoundingClientRect();
          const boxes = items.map(item => item.getBoundingClientRect());
          const visible = boxes.map((box, index) => ({ box, index })).filter(({ box }) =>
            box.width > 0 && Math.min(box.right, bounds.right) - Math.max(box.left, bounds.left) >= box.width * .9);
          return { bounds, boxes, visible };
        };
        const update = () => {
          if (group.hidden) return;
          const { visible } = measure();
          if (!visible.length) return;
          const overflow = track.scrollWidth > track.clientWidth + 3;
          browse.hidden = !overflow;
          previous.setAttribute('aria-disabled', String(!overflow || track.scrollLeft <= 3));
          next.setAttribute('aria-disabled', String(!overflow || track.scrollLeft + track.clientWidth >= track.scrollWidth - 3));
          const values = { start: visible[0].index + 1, end: visible.at(-1).index + 1, total: items.length };
          const template = values.start === values.end ? root.dataset.sliderRange.replace('{start}-{end}', '{start}') : root.dataset.sliderRange;
          const text = template.replace(/\{(start|end|total)\}/g, (_, key) => values[key]);
          const range = group.querySelector('[data-rel-range]');
          if (range.textContent !== text) range.textContent = text;
        };
        const go = index => {
          const { bounds, boxes } = measure();
          const box = boxes[Math.max(0, Math.min(index, items.length - 1))];
          closePickers();
          track.scrollTo({ left: track.scrollLeft + box.left - bounds.left - 2,
            behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        };
        const move = direction => {
          const { visible } = measure();
          if (!visible.length) return;
          go(visible[0].index + direction * visible.length);
        };
        previous.addEventListener('click', () => { if (previous.getAttribute('aria-disabled') !== 'true') move(-1); });
        next.addEventListener('click', () => { if (next.getAttribute('aria-disabled') !== 'true') move(1); });
        track.addEventListener('scroll', () => { closePickers(); update(); }, { passive: true });
        track.addEventListener('keydown', event => {
          if (event.target !== track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          if (event.key === 'Home') go(0);
          else if (event.key === 'End') go(items.length - 1);
          else move(event.key === 'ArrowRight' ? 1 : -1);
        });
        track.addEventListener('focusin', event => {
          const card = event.target.closest('[data-relationship-card]');
          if (!card) return;
          const { visible } = measure();
          const index = items.indexOf(card);
          if (!visible.some(item => item.index === index)) go(index);
        });
        sliders.set(group, update);
      });
      // Top-layer menus escape the scroll viewport; older browsers expand inline.
      root.querySelectorAll('[data-rel-picker]').forEach(picker => {
        const menu = picker.querySelector('.hhr-picker__menu');
        if (!menu.showPopover) return;
        menu.setAttribute('popover', 'manual');
        picker.addEventListener('toggle', () => {
          if (!picker.open || picker.closest('[data-rel-group]').hidden) {
            if (menu.matches(':popover-open')) menu.hidePopover();
            return;
          }
          root.querySelectorAll('[data-rel-picker]').forEach(other => { if (other !== picker) other.open = false; });
          const box = picker.querySelector('summary').getBoundingClientRect();
          const below = window.innerHeight - box.bottom - 12;
          const above = box.top - 12;
          const up = below < 180 && above > below;
          menu.style.width = `${Math.min(box.width, window.innerWidth - 24)}px`;
          menu.style.maxHeight = `${Math.max(44, Math.min(260, up ? above : below))}px`;
          menu.style.left = `${Math.max(12, Math.min(box.left, window.innerWidth - box.width - 12))}px`;
          menu.style.top = up ? 'auto' : `${box.bottom + 6}px`;
          menu.style.bottom = up ? `${window.innerHeight - box.top + 6}px` : 'auto';
          if (!menu.matches(':popover-open')) menu.showPopover();
        });
      });
    }
    const money = (price) => formatThemeMoney(Number(price), root.dataset.currency);
    const chosenCards = () => new Map(cards.filter(card => card.querySelector('[data-rel-select]'))
      .map(card => [offerFor(card)?.value, card])
      .filter(([id, card]) => id && selected.has(id) && !inCart.has(id) && offerFor(card).dataset.available === 'true'));
    const setGroup = (key, focus = false) => {
      const active = groups.find(group => group.dataset.relGroup === key) || groups[0];
      root.dataset.activeGroup = active.dataset.relGroup;
      groups.forEach(group => { group.hidden = group !== active; });
      tablist.querySelectorAll('[role="tab"]').forEach(tab => {
        const current = tab.dataset.relTab === active.dataset.relGroup;
        tab.setAttribute('aria-selected', String(current));
        tab.tabIndex = current ? 0 : -1;
        if (current && focus) tab.focus();
      });
      closePickers();
      footer.hidden = active.dataset.relGroup === 'alternatives';
      sliders.get(active)?.();
    };
    const sync = () => {
      cards.forEach(card => {
        const offer = offerFor(card);
        if (!offer) return;
        const available = offer.dataset.available === 'true';
        const inToolbox = inCart.has(offer.value);
        if (!available || inToolbox) selected.delete(offer.value);
        const button = card.querySelector('[data-rel-select]');
        if (button) {
          const included = selected.has(offer.value);
          button.hidden = false;
          button.disabled = busy || !available || inToolbox;
          button.setAttribute('aria-pressed', String(included));
          const label = inToolbox ? root.dataset.addedLabel : !available ? root.dataset.unavailableLabel : included ? root.dataset.removeLabel : root.dataset.addLabel;
          card.querySelector('[data-rel-select-label]').textContent = label;
          button.setAttribute('aria-label', `${label} - ${card.querySelector('.hhr-card__title').textContent.trim()} - ${offer.dataset.sku || offer.dataset.title}`);
          card.querySelector('[data-rel-plus]').hidden = included || inToolbox || !available;
          card.querySelector('[data-rel-minus]').hidden = !included;
          card.classList.toggle('is-selected', included);
        }
        card.querySelectorAll('input[type="radio"]').forEach(input => { input.disabled = busy; });
        card.dataset.currentVariant = offer.value;
        card.querySelector('[data-rel-price]').textContent = money(offer.dataset.price);
        card.querySelector('[data-rel-stock]').dataset.state = offer.dataset.stock;
        card.querySelector('[data-rel-stock-label]').textContent = offer.dataset.stockLabel;
        const sku = card.querySelector('[data-rel-sku]');
        sku.textContent = offer.dataset.sku || '';
        sku.hidden = !offer.dataset.sku;
        card.querySelectorAll('[data-rel-link]').forEach(link => { link.href = offer.dataset.url; });
        const choice = card.querySelector('[data-rel-choice]');
        if (choice) choice.textContent = offer.dataset.title;
        const image = card.querySelector('.hhr-card__photo img');
        if (image && offer.dataset.image && image.getAttribute('src') !== offer.dataset.image) {
          image.removeAttribute('srcset');
          image.src = offer.dataset.image;
          image.alt = offer.dataset.imageAlt;
        }
      });
      const chosen = chosenCards();
      const count = chosen.size;
      const noun = count === 1 ? root.dataset.itemSingular : root.dataset.itemPlural;
      root.querySelector('[data-rel-summary]').textContent = `${count} ${noun} ${root.dataset.selectedLabel}`;
      root.querySelector('[data-rel-total]').textContent = count ? money([...chosen.values()].reduce((sum, card) => sum + Number(offerFor(card).dataset.price), 0)) : '';
      submit.disabled = busy || count === 0;
      submit.setAttribute('aria-busy', String(busy));
    };
    const refreshCartState = async () => {
      const request = ++cartRequest;
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const cart = await response.json();
        if (request !== cartRequest || !root.isConnected) return;
        inCart = new Set(cart.items.map(item => String(item.variant_id)));
        sync();
      } catch (_) { /* Keep the last confirmed cart state when the network is unavailable. */ }
    };
    instances.set(root, { refreshCartState, closePickers, resize: () => { closePickers(); sliders.forEach(update => update()); } });
    if (groups.length > 0) {
      root.classList.add('is-tabbed');
      tablist.hidden = false;
      groups.forEach(group => {
        const tab = document.createElement('button');
        tab.type = 'button';
        tab.className = 'hhr__tab';
        tab.id = `${group.id}-tab`;
        tab.dataset.relTab = group.dataset.relGroup;
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', group.id);
        tab.setAttribute('aria-label', `${group.dataset.title}, ${group.dataset.count} ${Number(group.dataset.count) === 1 ? root.dataset.itemSingular : root.dataset.itemPlural}`);
        for (const [className, text] of [['hhr__tab-long', group.dataset.title], ['hhr__tab-short', group.dataset.shortTitle], ['hhr__tab-count', group.dataset.count]]) {
          const span = document.createElement('span');
          span.className = className;
          span.textContent = text;
          if (className === 'hhr__tab-count') span.setAttribute('aria-hidden', 'true');
          tab.append(span);
        }
        tablist.append(tab);
        group.setAttribute('role', 'tabpanel');
        group.setAttribute('aria-labelledby', tab.id);
        group.tabIndex = 0;
      });
    }
    root.classList.add('is-enhanced');
    setGroup(preferredGroup);
    sync();
    root.addEventListener('click', event => {
      const tab = event.target.closest('[data-rel-tab]');
      if (tab) { setGroup(tab.dataset.relTab, true); return; }
      const toggle = event.target.closest('[data-rel-select]');
      if (toggle && !toggle.disabled) {
        const id = offerFor(toggle.closest('[data-relationship-card]')).value;
        selected.has(id) ? selected.delete(id) : selected.add(id);
        message.hidden = true;
        sync();
      }
    });
    root.addEventListener('change', event => {
      if (!event.target.matches('input[type="radio"][data-rel-offer]') || busy) return;
      const card = event.target.closest('[data-relationship-card]');
      const previous = card.dataset.currentVariant;
      const wasSelected = selected.delete(previous);
      const next = offerFor(card);
      // A whole-product choice changes one exact SKU, never adds both sizes.
      cards.filter(other => other.dataset.productId === card.dataset.productId && other.dataset.fixed !== 'true').forEach(other => {
        other.querySelectorAll('input[type="radio"][data-rel-offer]').forEach(input => { input.checked = input.value === next.value; });
      });
      if (wasSelected && next.dataset.available === 'true') selected.add(next.value);
      const picker = card.querySelector('[data-rel-picker]');
      picker.open = false;
      sync();
      picker.querySelector('summary').focus();
    });
    root.addEventListener('keydown', event => {
      const picker = event.target.closest('[data-rel-picker]');
      if (event.key === 'Escape' && picker?.open) {
        event.preventDefault(); picker.open = false; picker.querySelector('summary').focus(); return;
      }
      const tab = event.target.closest('[role="tab"]');
      if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const index = groups.findIndex(group => group.dataset.relGroup === root.dataset.activeGroup);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? groups.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + groups.length) % groups.length;
      setGroup(groups[next].dataset.relGroup, true);
    });
    root.addEventListener('focusout', event => {
      const picker = event.target.closest('[data-rel-picker]');
      if (picker && event.relatedTarget && !picker.contains(event.relatedTarget)) picker.open = false;
    });
    submit.addEventListener('click', async () => {
      if (busy || submit.disabled) return;
      const items = [...chosenCards().keys()].map(id => ({ id: Number(id), quantity: 1 }));
      if (!items.length) return;
      busy = true; message.hidden = true; sync();
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections: ['cart-drawer'] })
        });
        const payload = await response.json();
        if (!response.ok || payload.status >= 400) throw new Error(payload.description || root.dataset.addError);
        items.forEach(item => { inCart.add(String(item.id)); selected.delete(String(item.id)); });
        message.textContent = root.dataset.addSuccess;
        message.hidden = false;
        await showUpdatedCart(payload, submit);
        announce(root.dataset.addSuccess);
      } catch (error) {
        message.textContent = error.message || root.dataset.addError;
        message.hidden = false;
        announce(message.textContent);
        await refreshCartState();
      } finally { busy = false; sync(); }
    });
  };
  const scan = (scope = document, preferredGroup) => {
    if (scope.matches?.('[data-pdp-relationships]')) init(scope, preferredGroup);
    scope.querySelectorAll('[data-pdp-relationships]').forEach(root => init(root, preferredGroup));
  };
  scan();
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('hh:relationships:load', event => scan(event.detail.root, event.detail.preferredGroup));
  document.addEventListener('hh:cart:updated', () => document.querySelectorAll('[data-pdp-relationships]').forEach(root => instances.get(root)?.refreshCartState()));
  window.addEventListener('resize', () => document.querySelectorAll('[data-pdp-relationships]').forEach(root => instances.get(root)?.resize()));
  window.addEventListener('scroll', () => document.querySelectorAll('[data-pdp-relationships]').forEach(root => instances.get(root)?.closePickers()), { passive: true });
  document.addEventListener('pointerdown', event => {
    document.querySelectorAll('[data-rel-picker][open]').forEach(picker => { if (!picker.contains(event.target)) picker.open = false; });
  });
})();
